import * as React from "react";
import styled from "styled-components";
import { DragPoints } from "../../../utils/dragAndDrop";

const ScrollItemsWrapper = styled.div`
	display: flex;
	transition-property: transform;
	height: 100%;
	width: 100%;
	transition-timing-function: cubic-bezier(0, 0, 0.25, 1);
`;

const { Children } = React;

interface BetterSwipeProps {
	className?: string;
	style?: React.CSSProperties;
	duration?: number;
	animationDuration?: number;
	onIndexChange?: (index: number) => void;
	swipeSensitive?: number;
	isAutoSwipe?: boolean;
	speedSensitive?: number;
	defaultIndex?: number;
}

interface BetterSwipeState {
	animationDuration: number;
	currentIndex: number;
	dragPoints: DragPoints;
	wrapperOffsetLeft: number;
	containerWidth?: number;
	startTime: number;
}

class BetterSwipe extends React.Component<BetterSwipeProps, BetterSwipeState> {
	static defaultProps = {
		duration: 4000,
		animationDuration: 600,
		swipeSensitive: 1,
		isAutoSwipe: true,
		speedSensitive: 320,
		style: {},
	};

	private scrollItemsWrapper: HTMLDivElement;
	private container: HTMLDivElement;

	state: BetterSwipeState = {
		currentIndex: this.props.defaultIndex || 1,
		animationDuration: 0,
		dragPoints: {},
		wrapperOffsetLeft: 0,
		startTime: 0,
	};

	timeoutId: NodeJS.Timeout | any;
	windowScrollTimeoutId: any = 0;
	isStop = false;

	onTransitionEnd = () => {
		const { children, onIndexChange } = this.props;
		const { currentIndex } = this.state;
		const updateState = { animationDuration: 0 } as BetterSwipeState;
		const childrenCount = Children.count(children);

		if (childrenCount + 1 === currentIndex) {
			updateState.currentIndex = 1;
		} else if (currentIndex === 0) {
			updateState.currentIndex = childrenCount;
		}

		this.setState(updateState as any);

		if (typeof onIndexChange !== "undefined") {
			let index = currentIndex - 1;
			if (currentIndex === 0) {
				index = childrenCount - 1;
			} else if (currentIndex === childrenCount + 1) {
				index = 0;
			}
			onIndexChange(index);
		}
	};

	getNodeCount() {
		return Children.count(this.props.children) + 2;
	}

	onTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
		const { animationDuration } = this.state;

		if (animationDuration === 0 && !this.isDraging()) {
			this.stopAutoScroll();

			const touch = e.touches[0];
			const dragPoints = {
				startPoint: {
					x: touch.clientX,
					y: touch.clientY,
				},
			};
			this.setState({
				dragPoints,
				wrapperOffsetLeft: this.scrollItemsWrapper.getBoundingClientRect().left,
				startTime: new Date().getTime(),
			});
		}
	};

	onTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
		if (this.isDraging()) {
			const touch = e.touches[0];
			const endPoint = {
				x: touch.clientX,
				y: touch.clientY,
			};
			const { startPoint } = this.state.dragPoints as any;
			if (Math.abs(endPoint.y - startPoint.y) > Math.abs(endPoint.x - startPoint.x)) {
				this.setState({ dragPoints: {} });
				this.autoScroll();
			} else {
				const dragPoints = {
					...this.state.dragPoints,
					endPoint,
				};
				this.setState({ dragPoints });
				e.stopPropagation();
			}
		}
	};

	onTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
		const { dragPoints, currentIndex, containerWidth, startTime } = this.state;

		const distance = this.getDistance(dragPoints);
		if (this.isDraging() && distance) {
			let updateState = {
				dragPoints: {},
			} as BetterSwipeState;
			const { animationDuration = 0, swipeSensitive, speedSensitive } = this.props;
			const distanceAbs = Math.abs(distance);
			const endTime = new Date().getTime();
			const slideSpeed = (distanceAbs * 1000) / (endTime - startTime);
			if (
				distanceAbs >
					Math.ceil(((containerWidth as number) * (swipeSensitive as number)) / 2) ||
				slideSpeed > (speedSensitive as number)
			) {
				const scrollNumber = Math.ceil(distanceAbs / (containerWidth as number));
				if (distance < 0) {
					updateState.currentIndex = currentIndex - scrollNumber;
				} else {
					updateState.currentIndex = currentIndex + scrollNumber;
				}
			}

			updateState.currentIndex = Math.max(0, updateState.currentIndex);
			updateState.currentIndex = Math.min(this.getNodeCount() - 1, updateState.currentIndex);

			const duration = Math.ceil(
				(distanceAbs / (containerWidth as number)) * animationDuration,
			);

			const nextAnimationDuration =
				duration > animationDuration
					? (animationDuration as number) - (duration % animationDuration)
					: Math.max(50, duration);
			updateState = {
				...updateState,
				animationDuration: nextAnimationDuration,
			};

			this.setState(updateState);
			this.autoScroll();
		}
		e.stopPropagation();
	};

	onWindowScroll = () => {
		clearTimeout(this.windowScrollTimeoutId);
		this.windowScrollTimeoutId = setTimeout(() => {
			if (this.isCotainerInCurrentScreen()) {
				this.isStop = false;
			} else {
				this.isStop = true;
			}
		}, 80);
	};

	onResize = () => {
		this.setState({ containerWidth: this.container.getBoundingClientRect().width });
	};

	isCotainerInCurrentScreen() {
		return this.container.getBoundingClientRect().bottom >= 0;
	}

	scrollItemsWrapperStyle() {
		const { currentIndex, dragPoints, animationDuration, containerWidth } = this.state;
		const style: React.CSSProperties = {};
		style.width = `${this.getNodeCount()}00%`;

		const wrapperOffsetLeft = -1 * currentIndex * (containerWidth as number);
		if (this.isDraging()) {
			const distance = this.getDistance(dragPoints);
			style.transform = `translate3d(${wrapperOffsetLeft - distance}px,0,0)`;
		} else {
			style.transform = `translate3d(${wrapperOffsetLeft}px,0,0)`;
			style.transitionDuration = `${animationDuration}ms`;
		}
		style.WebkitTransform = style.transform;
		style.transitionTimingFunction = "ease-out";
		return style;
	}

	getDistance = (dragPoints: DragPoints): number => {
		const { endPoint, startPoint } = dragPoints;
		if (typeof endPoint !== "undefined" && typeof startPoint !== "undefined") {
			return startPoint.x - endPoint.x;
		}
		return 0;
	};

	isDraging(): boolean {
		return !!this.state.dragPoints.endPoint || !!this.state.dragPoints.startPoint;
	}

	bindContainerRef = (me: HTMLDivElement) => {
		this.container = me;
	};

	bindScrollItemsWrapperRef = (me: HTMLDivElement) => {
		this.scrollItemsWrapper = me;
	};

	render() {
		const { className, children } = this.props;
		const childrenNodes = Children.toArray(children);

		if (childrenNodes.length === 1) {
			return (
				<div className={className} ref={this.bindContainerRef}>
					{children}
				</div>
			);
		}

		return (
			<div
				className={className}
				ref={this.bindContainerRef}
				onTouchStart={this.onTouchStart}
				onTouchMove={this.onTouchMove}
				onTouchEnd={this.onTouchEnd}
				onTouchCancel={this.onTouchEnd}>
				<ScrollItemsWrapper
					ref={this.bindScrollItemsWrapperRef}
					style={this.scrollItemsWrapperStyle()}
					onTransitionEnd={this.onTransitionEnd}>
					{childrenNodes.slice(-1)[0]}
					{children}
					{childrenNodes[0]}
				</ScrollItemsWrapper>
			</div>
		);
	}

	componentDidMount() {
		this.autoScroll();
		window.addEventListener("scroll", this.onWindowScroll);
		window.addEventListener("resize", this.onResize);
		this.setState({ containerWidth: this.container.getBoundingClientRect().width });
	}

	componentWillUnmount() {
		this.stopAutoScroll();
		clearTimeout(this.windowScrollTimeoutId);
		window.removeEventListener("scroll", this.onWindowScroll);
		window.removeEventListener("resize", this.onResize);
	}

	autoScroll() {
		const { isAutoSwipe, duration } = this.props;
		if (isAutoSwipe) {
			clearTimeout(this.timeoutId);
			this.timeoutId = setTimeout(() => {
				if (!this.isStop && document.visibilityState === "visible") {
					const { currentIndex } = this.state;
					this.scrollByIndex(currentIndex + 1);
				}
				this.autoScroll();
			}, duration);
		}
	}

	stopAutoScroll() {
		const { isAutoSwipe } = this.props;
		if (isAutoSwipe) {
			clearTimeout(this.timeoutId);
		}
	}

	scrollByIndex(index: number) {
		this.setState({
			currentIndex: index,
			animationDuration: this.props.animationDuration as number,
		});
	}
}

export default BetterSwipe;
